import { useIntl } from "react-intl";
import React, { useState, useEffect, useRef, CSSProperties, useCallback } from "react";
import { Segment, Checkbox, Button, CheckboxProps, List } from "semantic-ui-react";

//Common
import SearchBox from "react-lib/apps/common/SearchBox";
import { applyTransformStyle } from "./ModalItemFilterCustom";

type ListChecked = {
  group: string;
  name: any[];
  is_selected: boolean;
  is_show: boolean;
  items: any[];
};

type ModalItemFilterLevelCustomProps = {
  data: ListChecked[];
  level1: string;
  level2: string;
  style?: CSSProperties;
  title: string;
  target?: string;
  classNameZoom?: string | string[];
  // Callback
  onClose?: () => any;
  onSelect?: (list: any[], filterArray?: string[]) => any;
};

const ModalItemFilterLevelCustomInitial: ModalItemFilterLevelCustomProps = {
  data: [],
  level1: "",
  level2: "",
  style: {},
  title: "",
  // Callback
  onClose: () => null,
  onSelect: () => null,
};

const ModalItemFilterLevelCustom: React.FC<ModalItemFilterLevelCustomProps> = (props) => {
  const intl = useIntl();

  const [items, setItems] = useState<ListChecked[]>([]);
  const [isSelectAll, setIsSelectAll] = useState<boolean>(true);

  // Ref
  const segmentHoverRef = useRef<any>(false);

  const handleMouseClick = useCallback((e: any) => {
    if (!segmentHoverRef.current) {
      props.onClose?.();

      setItems([]);
      setIsSelectAll(false);

      segmentHoverRef.current = false;
    }
  }, []);

  const handleOnScroll = useCallback(() => {
    applyTransformStyle(props.target, props.classNameZoom);
  }, []);

  const removeEventListener = useCallback(() => {
    document.removeEventListener("click", handleMouseClick);
    document.removeEventListener("scroll", handleOnScroll, true);
    window.removeEventListener("resize", handleOnScroll);
  }, [handleMouseClick, handleOnScroll]);

  useEffect(() => {
    removeEventListener();

    document.addEventListener("click", handleMouseClick);
    document.addEventListener("scroll", handleOnScroll, true);
    window.addEventListener("resize", handleOnScroll);

    handleOnScroll();
    return () => {
      removeEventListener();
    };
  }, [props.target, props.classNameZoom, removeEventListener]);

  useEffect(() => {
    const cloneItems = [...props.data];
    cloneItems?.forEach((item: any) => {
      item.items?.forEach((sub: any) => {
        sub.is_show = true;
      });
      item.is_show = true;
      item.is_selected = item.items.every((i: any) => i.is_selected);
    });
    setItems(cloneItems);
  }, [props.data]);

  useEffect(() => {
    if (items.every((item) => item.is_selected)) {
      setIsSelectAll(true);
    } else {
      if (isSelectAll) {
        setIsSelectAll(false);
      }
    }
  }, [items]);

  const handleChangeLevel1 = (index: number, checked: boolean) => {
    const cloneItems = [...items];
    cloneItems[index].is_selected = checked;

    cloneItems[index].items?.forEach((item: any) => {
      item.is_selected = checked;
    });
    setItems(cloneItems);
  };

  const handleChangeLevel2 = (index: number, sindex: number, checked: boolean) => {
    const cloneItems = [...items];
    cloneItems[index].items[sindex].is_selected = checked;

    cloneItems[index].is_selected = cloneItems[index].items.every((item: any) => item.is_selected);

    setItems(cloneItems);
  };

  const handleToggleSelectAll = (_event: any, { checked }: CheckboxProps) => {
    const cloneItems = [...items];
    cloneItems.forEach((item: any) => {
      item.is_selected = checked;
      item.items.forEach((sub: any) => {
        sub.is_selected = checked;
      });
    });
    setItems(cloneItems);
    setIsSelectAll(!!checked);
  };

  const handleGetSearchOptions = ({ searchText = "" }) => {
    const cloneItems = [...items];
    cloneItems.forEach((item: any) => {
      item.items?.forEach((sub: any) => {
        const lv2Checked: boolean = sub?.[props.level2].toLowerCase().includes(searchText.toLowerCase());
        sub.is_show = lv2Checked;
        sub.is_selected = lv2Checked;
      });
      const lv1Checked: boolean = item?.[props.level1].toLowerCase().includes(searchText.toLowerCase()) || item.items.some((item: any) => item.is_show);
      item.is_show = lv1Checked;
      item.is_selected = lv1Checked;
    });

    setItems(cloneItems);

    return [[], null];
  };

  const handleFilter = () => {
    let result: any[] = [];
    const cloneItems = [...items];

    cloneItems.forEach((item: any) => {
      item.items.forEach((sub: any) => {
        if (sub.is_show) {
          result.push({
            name: sub.name,
            is_selected: sub.is_selected,
            [props.level1]: item[props.level1],
          });
        }
      });
    });

    props.onSelect?.(result);
  };

  return (
    <Segment
      className="scn-modal-item-filter"
      style={{
        position: "fixed",
        background: "#f2f2f2",
        zIndex: 9,
        width: "300px",
        opacity: 0,
        maxHeight: "350px",
        overflowY: "scroll",
        overflowX: "hidden",
        ...props.style,
      }}
      onMouseOver={() => (segmentHoverRef.current = true)}
      onClick={() => (segmentHoverRef.current = true)}
      onMouseLeave={() => (segmentHoverRef.current = false)}
    >
      <div style={{ display: "flex", marginBottom: "6px" }}>
        {intl.formatMessage({ id: "cukey79" })} <label style={{ fontWeight: "bold", paddingLeft: "5px" }}>{props.title}</label>
      </div>
      <SearchBox inputStyle={{ width: "100%" }} onGetSearchOptions={handleGetSearchOptions} minCharacters={0} />
      <Checkbox
        label="Select All"
        style={{ fontWeight: "bold", padding: "6px", width: "100%", border: "1px solid #e1e1e1" }}
        checked={isSelectAll}
        onChange={handleToggleSelectAll}
      />

      <List style={{ paddingLeft: "7px" }}>
        {items.map((item, index) => {
          if (item.is_show) {
            return (
              <List.Item key={index} style={{ paddingBottom: "15px" }}>
                <Checkbox
                  label={(item as any)[props.level1]}
                  checked={item.is_selected}
                  onChange={(e, { checked }) => {
                    handleChangeLevel1(index, !!checked);
                  }}
                />
                <List.List>
                  {item.items?.map((sub: any, sindex: number) => {
                    if (sub.is_show) {
                      return (
                        <List.Item key={sindex}>
                          <Checkbox
                            label={sub[props.level2]}
                            checked={sub.is_selected}
                            onChange={(e, { checked }) => {
                              handleChangeLevel2(index, sindex, !!checked);
                            }}
                          />
                        </List.Item>
                      );
                    }

                    return null;
                  })}
                </List.List>
              </List.Item>
            );
          } else {
            return <></>;
          }
        })}
      </List>

      <Button fluid color="blue" onClick={handleFilter}>
        {intl.formatMessage({ id: "cukey78" })}
      </Button>
    </Segment>
  );
};

ModalItemFilterLevelCustom.defaultProps = ModalItemFilterLevelCustomInitial;

export default React.memo(ModalItemFilterLevelCustom);
